import { MAX_BOOKING_REDIRECT_SECONDS } from "src/modules/signup/helpers/bookingEmbed";
import {
  Application,
  ApplicationFormData,
  ApplicationSelfServiceFormValues,
  ApplicationSelfServiceSettings,
  ApplicationSettings,
  ApplicationSettingsFormData,
  CreateAdminApplicationRequestData,
  UpdateAdminApplicationRequestData,
} from "../types";

export function buildFormData(data?: Application): ApplicationFormData<ApplicationSettingsFormData> {
  const settings: ApplicationSettings = JSON.parse(data?.settings || "{}");

  return {
    id: data?.id || "",
    name: data?.name || "",
    domain: data?.domain || "",
    priceForSMS: data?.priceForSMS || 0,
    priceForMMS: data?.priceForMMS || 0,
    priceForLookup: data?.priceForLookup || 0,
    priceForAlert: data?.priceForAlert || 0,
    settings: {
      ...settings,
      selfService: buildSelfServiceValues(settings),
    },
    defaultUseCase: data?.defaultUseCase || "",
    defaultSubUseCases: data?.defaultSubUseCases || [],
  };
}

export function buildRequestData(
  data: ApplicationFormData<ApplicationSettingsFormData>
): CreateAdminApplicationRequestData | UpdateAdminApplicationRequestData {
  return {
    name: data.name,
    domain: data.domain,
    priceForSMS: data.priceForSMS,
    priceForMMS: data.priceForMMS,
    priceForLookup: data.priceForLookup,
    priceForAlert: data.priceForAlert,
    settings: JSON.stringify({
      ...data.settings,
      selfService: buildSelfServiceSettings(data.settings.selfService),
    }),
    defaultUseCase: data.defaultUseCase,
    defaultSubUseCases: data.defaultSubUseCases,
  };
}

const DEFAULT_BOOKING_REDIRECT_SECONDS = 3;

// Matches the backend fallback for a missing price: a saved form and an untouched application show the same.
const DEFAULT_LOCAL_NUMBER_PRICE_USD = 10;

/**
 * The redirect field as the number of seconds to store, or null for anything outside 0 to
 * `MAX_BOOKING_REDIRECT_SECONDS`. The form validates the field only while it is shown, so the bound is held here.
 */
export const parseBookingRedirectSeconds = (value: string): number | null => {
  const seconds = value.trim();
  if (!/^\d+$/.test(seconds)) return null;

  return Number(seconds) <= MAX_BOOKING_REDIRECT_SECONDS ? Number(seconds) : null;
};

/** Empty is allowed and means no redirect; anything else must be a delay the booking screen would honour. */
export const validateBookingRedirectSeconds = (value?: string): string | true =>
  (value ?? "").trim().length === 0
  || parseBookingRedirectSeconds(value ?? "") !== null
  || `Enter a whole number from 0 to ${MAX_BOOKING_REDIRECT_SECONDS}.`;

function buildSelfServiceValues(settings: ApplicationSettings): ApplicationSelfServiceFormValues {
  // Off with the default price reads the same to the backend as an absent block, so filling one in for the
  // applications configured before the funnel existed changes nothing but gives the inputs a shape.
  const selfService: ApplicationSelfServiceSettings = settings.selfService ?? {
    enabled: false,
    localNumberPriceUsd: DEFAULT_LOCAL_NUMBER_PRICE_USD,
  };
  const { abandonmentCheckpointHours, bookingRedirectSeconds, ...stored } = selfService;
  const [first, second] = abandonmentCheckpointHours ?? [];

  return {
    ...stored,
    enabled: stored.enabled ?? false,
    localNumberPriceUsd: stored.localNumberPriceUsd ?? DEFAULT_LOCAL_NUMBER_PRICE_USD,
    freeCredits: stored.freeCredits ?? 0,
    vertical: stored.vertical ?? "",
    bookingUrl: stored.bookingUrl ?? "",
    // Undefined is an application nobody has decided for, and opens on the default. Null is a redirect the
    // admin switched off, kept apart so that the next save of the form does not switch it back on.
    bookingRedirectSeconds:
      bookingRedirectSeconds === undefined
        ? String(DEFAULT_BOOKING_REDIRECT_SECONDS)
        : bookingRedirectSeconds === null ? "" : String(bookingRedirectSeconds),
    sampleMessage1: stored.sampleMessage1 ?? "",
    sampleMessage2: stored.sampleMessage2 ?? "",
    firstReminderHours: first ?? "",
    secondReminderHours: second ?? "",
  };
}

function buildSelfServiceSettings(values?: ApplicationSelfServiceFormValues) {
  if (!values) return undefined;

  const {
    firstReminderHours,
    secondReminderHours,
    sampleMessage1,
    sampleMessage2,
    vertical,
    bookingUrl,
    bookingRedirectSeconds,
    ...stored
  } = values;
  const hours = [firstReminderHours, secondReminderHours].filter(
    (hours): hours is number => typeof hours === "number" && hours > 0
  );

  return {
    ...stored,
    // A cleared number input shows 0 but holds null, and the backend reads null as its own default, which for
    // the price is $10: the strike-through would come back after a save that showed it off.
    localNumberPriceUsd: stored.localNumberPriceUsd ?? 0,
    freeCredits: stored.freeCredits ?? 0,
    // Written as null rather than left out: `buildSelfServiceValues` reopens a missing key on the default. Left
    // out only where it is that default on an application without the funnel, which keeps such JSON untouched.
    ...(!stored.enabled && bookingRedirectSeconds.trim() === String(DEFAULT_BOOKING_REDIRECT_SECONDS)
      ? {}
      : { bookingRedirectSeconds: parseBookingRedirectSeconds(bookingRedirectSeconds) }),
    // The text fields are left out rather than written empty: absent and empty mean the same to the backend,
    // and an application nobody has touched keeps the JSON it had.
    ...(vertical.trim().length > 0 ? { vertical: vertical.trim() } : {}),
    ...(bookingUrl.trim().length > 0 ? { bookingUrl: bookingUrl.trim() } : {}),
    ...(sampleMessage1.trim().length > 0 ? { sampleMessage1: sampleMessage1.trim() } : {}),
    ...(sampleMessage2.trim().length > 0 ? { sampleMessage2: sampleMessage2.trim() } : {}),
    ...(hours.length > 0 ? { abandonmentCheckpointHours: hours } : {}),
  };
}
